import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { formatDateAsDayMonthYear } from '../../../../utils/dateFormatter';
import { titleize } from '../../../../utils/helpers';
import { formatPercentage, formatTwoDecimals } from '../../../../utils/numberFormatter';

const hoverStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Participants: show 10 pairings of company name and stake
const participationColumns = Array.from({ length: 10 }, (_, index) => {
  const participationNumber = index + 1;
  return [
    {
      field: `companyName${participationNumber}`,
      filterable: false,
      sortable: false,
      headerName: `Company #${participationNumber}`,
      renderCell: params => params.row.asset?.currentParticipations?.[index]?.company?.name,
      width: 150,
    },
    {
      field: `percentStake${participationNumber}`,
      filterable: false,
      sortable: false,
      headerName: `Company #${participationNumber} Stake`,
      renderCell: params => {
        const percentStake = params.row.asset?.currentParticipations?.[index]?.percentStake;
        return percentStake ? `${percentStake}%` : '';
      },
      width: 150,
    },
  ];
}).flat();

export const COLUMNS = [
  {
    field: 'country',
    filterable: false,
    headerName: 'Country',
    renderCell: params => params.row.country.displayName,
    width: 200,
  },
  {
    field: 'asset',
    filterable: false,
    headerName: 'Asset',
    renderCell: params => {
      const { country } = params.row || { country: {} };
      const { legacyId, displayName } = params.row.asset || { legacyId: null, displayName: null };
      const assetName = displayName || params.row.assetName || 'N/A'; // Fallback to assetName from MA Opportunity if asset is deleted

      const baseUrl = window.location.origin;
      const link =
        country?.isoCode && legacyId ? `${baseUrl}/valuations/${country.isoCode}/${legacyId}` : '';

      return (
        <Box display='flex' alignItems='center'>
          <Link
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <Typography color='secondary' style={{ display: 'contents', alignItems: 'center' }}>
              {assetName}
            </Typography>
            {link && <OpenInNewIcon fontSize='small' color='action' />}
          </Link>
        </Box>
      );
    },
    width: 200,
  },
  {
    field: 'group',
    filterable: false,
    headerName: 'Grouping',
    renderCell: params => params.row.group,
    width: 200,
  },
  {
    field: 'officiallyOnMarket',
    filterable: false,
    headerName: 'Known Opportunity, Market Speculation or Welligence Viewpoint?',
    renderCell: params => params.row.officiallyOnMarket,
    width: 280,
  },
  {
    field: 'comment',
    headerName: 'Comment',
    width: 400,
    filterable: false,
    renderCell: cell => {
      const value = cell.row?.comment;
      return (
        <Tooltip title={value || ''} placement='bottom-start'>
          <span style={hoverStyles}>{value}</span>
        </Tooltip>
      );
    },
  },
  {
    field: 'dateOfEntry',
    filterable: false,
    headerName: 'Date Entered',
    renderCell: params => formatDateAsDayMonthYear(params.row.dateOfEntry),
    width: 200,
  },
  {
    field: 'basin',
    filterable: false,
    headerName: 'Basin',
    renderCell: params => params.row.asset?.basin?.name,
    width: 200,
  },
  {
    field: 'sellerCompany',
    filterable: false,
    headerName: 'Seller',
    renderCell: params => params.row.sellerCompany?.name || params.row.sellerCompanyName,
    width: 200,
  },
  {
    field: 'sellerStake',
    filterable: false,
    headerName: 'Seller Stake on Offer',
    renderCell: params => formatPercentage(params.row.sellerStake),
    width: 200,
  },
  {
    field: 'operatedStake',
    filterable: false,
    headerName: 'Operated Stake Available',
    renderCell: params => (params.row.operatedStake === true ? 'Yes' : 'No'),
    width: 200,
  },
  {
    field: 'sellerMotivation',
    filterable: false,
    headerName: 'Seller Motivation',
    renderCell: params => params.row.sellerMotivation,
    width: 200,
  },
  {
    field: 'npv',
    filterable: false,
    headerName: 'NPV10',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.npv),
  },
  {
    field: 'remainingReserves',
    filterable: false,
    headerName: 'WEA Reserves (MMboe)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.remainingReserves),
  },
  {
    field: 'remainingLiquidReserves',
    filterable: false,
    headerName: 'WEA Liquids Reserves (MMbbl)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.remainingLiquidReserves),
  },
  {
    field: 'remainingGasReserves',
    filterable: false,
    headerName: 'WEA Gas Reserves (bcf)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.remainingGasReserves),
  },
  {
    field: 'pricePerBarrel',
    filterable: false,
    headerName: 'WEA Price/Barrel (US$/boe)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.asset?.pricePerBarrel),
  },
  {
    field: 'estimatedRecentProduction',
    filterable: false,
    headerName: 'Recent Production (boe/d)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.estimatedRecentProduction),
  },
  {
    field: 'pricePerFlowingBarrel',
    filterable: false,
    headerName: 'WEA Price/Flowing Barrel (US$/boe)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.pricePerFlowingBarrel),
  },
  {
    field: 'currentEmissionsIntensity',
    filterable: false,
    headerName: 'Full Year Emissions Intensity (kgCO2e/boe)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.asset?.currentEmissionsIntensity),
  },
  {
    field: 'remainingEmissionsIntensity',
    filterable: false,
    headerName: 'Remaining Emissions Intensity (kgCO2e/boe)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.asset?.remainingEmissionsIntensity),
  },
  {
    field: 'remainingCapex',
    filterable: false,
    headerName: 'Remaining Capex (US$MM)',
    width: 200,
    renderCell: params => formatTwoDecimals(params.row.remainingCapex),
  },
  {
    field: 'hydrocarbonType',
    filterable: false,
    headerName: 'Hydrocarbon Type',
    renderCell: params => titleize(params.row.asset?.hydrocarbonType),
    width: 200,
  },
  {
    field: 'licenseExpirationDate',
    filterable: false,
    headerName: 'License Expiry',
    renderCell: params => formatDateAsDayMonthYear(params.row.asset?.licenseExpirationDate),
    width: 200,
  },
  {
    field: 'resourceTheme',
    filterable: false,
    headerName: 'Resource Theme',
    renderCell: params => params.row.resourceTheme,
    width: 200,
  },
  {
    field: 'shoreStatus',
    filterable: false,
    headerName: 'Shore Status',
    renderCell: params => params.row.asset?.shoreStatus?.name,
    width: 200,
  },

  {
    field: 'developmentStatus',
    filterable: false,
    headerName: 'Development Status',
    renderCell: params => params.row.asset?.developmentStatus?.name,
    width: 200,
  },
  ...participationColumns,
];
